import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/shared';
import type { Picked } from '../../components/shared/EntityPicker';
import { tCalCreate } from '../../lib/calendar-create-i18n';
import type { CalendarInviteSource, InviteMember } from './calendar-invite-source';
import './RecurrenceSettings.css';

/**
 * 「[팀명] 팀원 모두 초대」 단추 (2026-10-13 이식 — 옛 가지 4b3338c66 · a881fa6a5 · 9b12617fe · AOS b7c844e7e).
 *
 * ```
 * 누르면   그 캘린더의 구성원을 불러와(`source.load`) 참석자 «끝에» 붙인다
 * 빼는 것  나 · 이미 참석자인 사람 · 겹침 · 빈 id
 * 상한     서버 100명(`calendar-helpers.ts` attendeeUserIds maxItems) — 넘는 사람은 안 넣고 몇 명인지 말한다
 * 실패     한 줄로 알린다(창은 그대로)
 * 칩       번호가 아니라 «이름»으로 — 구성원 응답의 이름을 그대로 싣는다(a881fa6a5)
 * ```
 * ⚠ 단추는 «어디서» 불러오는지 모른다(`source`) — 조직도 팀 캘린더도 같은 단추를 쓴다.
 */
export interface TeamInviteButtonProps {
  source: CalendarInviteSource;
  /** 지금 참석자로 «펼친» 사람 id(팀째 고른 것 포함) — 겹침·상한 셈의 기준. */
  existingIds: readonly string[];
  selfId: string | null;
  max: number;
  onAdd: (people: Picked[]) => void;
  disabled?: boolean;
}

/** 순수 셈 — 무엇을 더하고 몇 명이 넘쳤나. 단추와 시험이 같은 것을 쓴다. */
export function planTeamInvite(
  members: readonly InviteMember[],
  existingIds: readonly string[],
  selfId: string | null,
  max: number,
): { add: InviteMember[]; over: number } {
  const seen = new Set(existingIds.map(String));
  if (selfId) seen.add(String(selfId));
  const fresh: InviteMember[] = [];
  for (const m of members) {
    const id = String(m.id ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    fresh.push({ id, name: m.name || id });
  }
  const room = Math.max(0, max - existingIds.length);
  return { add: fresh.slice(0, room), over: Math.max(0, fresh.length - room) };
}

export function TeamInviteButton({ source, existingIds, selfId, max, onAdd, disabled = false }: TeamInviteButtonProps) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  // 캘린더를 바꾸면 지난 알림 줄을 지운다 — 다른 팀의 결과가 남아 보이면 안 된다.
  useEffect(() => { setNote(null); }, [source.key]);

  async function invite() {
    setBusy(true);
    setNote(null);
    try {
      const members = await source.load();
      if (!alive.current) return;
      const { add, over } = planTeamInvite(members, existingIds, selfId, max);
      if (add.length > 0) onAdd(add.map((m) => ({ kind: 'person', id: m.id, name: m.name })));
      const lines: string[] = [];
      lines.push(add.length > 0 ? tCalCreate('invite.added', { n: add.length }) : tCalCreate('invite.none_new'));
      if (over > 0) lines.push(tCalCreate('invite.over', { max, n: over }));
      setNote(lines.join(' '));
    } catch {
      if (alive.current) setNote(tCalCreate('invite.failed'));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  return (
    <div className="cal-team-invite">
      <Button type="button" variant="secondary" size="sm" onClick={() => { void invite(); }} disabled={disabled || busy}>
        {busy ? tCalCreate('invite.busy') : tCalCreate('invite.team_all', { team: source.teamName })}
      </Button>
      {note && <span className="cal-team-invite__note" role="status">{note}</span>}
    </div>
  );
}
